import { Injectable } from '@angular/core';
import {
  Action,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { StoresControllerService } from '@osos/shared/api';
import {
  DataEntityPresenterModel,
  DataStorePresenterModel,
  DataStoreTypeEnum,
  LoadingIndicator,
} from '@osos/shared/types';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { EntityState } from '../entity-data-state/entity.state';
import { LoadingActions } from '../loading/loading.actions';
import { DataStoreActions } from './data-store.action';

export interface DataStoresStateModel {
  presentedDataStores?: { [key: string]: DataStorePresenterModel[] };
  loading?: LoadingIndicator;
}

@State<DataStoresStateModel>({
  name: 'dataStores',
  defaults: {
    presentedDataStores: {},
    loading: new LoadingIndicator(),
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class DataStoreState {
  constructor(private dataStoresService: StoresControllerService) {}

  // --------------------------------- SELECTORS ------------------------------

  /**
   * select transitory modules.
   * it's valid to be readed once after trigger TransitoryFetchModules action
   */

  @Selector([DataStoreState, EntityState.selectedEntity])
  static presentedDataStores(
    state: DataStoresStateModel,
    selectedEntity: DataEntityPresenterModel
  ): DataStorePresenterModel[] {
    return selectedEntity ? state.presentedDataStores[selectedEntity.id] : [];
  }

  @Selector([DataStoreState])
  static loading(state: DataStoresStateModel): boolean {
    return state.loading.fetch;
  }

  // --------------------------------- ACTIONS --------------------------------

  @Action(DataStoreActions.FetchEntityStores, {
    cancelUncompleted: true,
  })
  fetchDataStores(
    { setState }: StateContext<DataStoresStateModel>,
    { entityId }: DataStoreActions.FetchEntityStores
  ): Observable<DataStorePresenterModel[]> {
    setState(
      patch<DataStoresStateModel>({
        presentedDataStores: patch({
          [entityId]: [],
        }),
        loading: patch<LoadingIndicator>({
          fetch: true,
        }),
      })
    );
    return this.dataStoresService
      .getAllStores({
        entityId,
      })
      .pipe(
        tap((stores: DataStorePresenterModel[]) => {
          setState(
            patch<DataStoresStateModel>({
              presentedDataStores: patch({
                [entityId]: stores,
              }),
            })
          );
        }),
        finalize(() => {
          setState(
            patch<DataStoresStateModel>({
              loading: patch<LoadingIndicator>({
                fetch: false,
              }),
            })
          );
        })
      );
  }

  @Action(DataStoreActions.CreateEntityStore)
  createDataStore(
    ctx: StateContext<DataStoresStateModel>,
    { entityId, store }: DataStoreActions.CreateEntityStore
  ): Observable<DataStorePresenterModel> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));

    ctx.setState(
      patch<DataStoresStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      })
    );

    return this.dataStoresService
      .createStore({
        entityId,
        body: {
          dataEntity: entityId,
          name: store.name,
          spaces: store.spaces,
          weight: store.weight,
          description: store.description,
          type: DataStoreTypeEnum.Shared,
        },
      })
      .pipe(
        tap((store) => {
          ctx.setState(
            patch<DataStoresStateModel>({
              presentedDataStores: patch({
                [entityId]: append<DataStorePresenterModel>([store]),
              }),
            })
          );
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<DataStoresStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            })
          );
        })
      );
  }

  @Action(DataStoreActions.UpdateEntityStore)
  updateDataStore(
    ctx: StateContext<DataStoresStateModel>,
    { entityId, store, storeId }: DataStoreActions.UpdateEntityStore
  ) {
    ctx.setState(
      patch<DataStoresStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      })
    );
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));

    return this.dataStoresService
      .updateStore({
        entityId,
        storeId,
        body: store,
      })
      .pipe(
        tap((store) => {
          ctx.setState(
            patch<DataStoresStateModel>({
              presentedDataStores: patch({
                [entityId]: updateItem<DataStorePresenterModel>(
                  (s) => s.storeId === store.storeId,
                  store
                ),
              }),
            })
          );
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<DataStoresStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            })
          );
        })
      );
  }
}
